<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard" :stacked="scheduleModalVisible">
        <template v-slot:header>Group Status</template>
        <template v-slot:subheader
            >Status change for <strong>{{ props.groupName }}</strong></template
        >

        <page-loading v-if="loading" class="py-8" />

        <vx-alert v-else-if="scheduledChange" data-test="scheduled-status-callout" :closeable="false">
            <div class="text-sm">
                <div class="font-semibold">Status Change Scheduled</div>
                <div>
                    Status will change from
                    <strong class="capitalize">{{ currentStatus }}</strong>
                    to
                    <strong class="capitalize">{{ scheduledChange.target_status }}</strong>
                    on {{ scheduledDateFormatted }}
                </div>
            </div>
        </vx-alert>

        <group-status-radios
            v-else-if="currentStatus"
            v-model="selected"
            :current-status="currentStatus"
            :is-billable="isBillable"
        />

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <template v-if="scheduledChange">
                    <vx-button
                        :disabled="cancelling"
                        :loading="cancelling"
                        type="button"
                        class="grow"
                        size="lg"
                        color="primary"
                        data-test="scheduled-cancel-button"
                        @click="cancelScheduled"
                    >
                        Cancel Scheduled Change
                        <font-awesome-icon widthAuto :icon="faCircleXmark" />
                    </vx-button>
                </template>
                <template v-else>
                    <vx-button
                        :disabled="!canSchedule"
                        :loading="submitting"
                        type="button"
                        size="lg"
                        color="muted"
                        data-test="group-status-schedule"
                        @click.prevent="scheduleModalVisible = true"
                    >
                        <font-awesome-icon widthAuto :icon="faCalendar" />
                        Schedule for Later
                    </vx-button>
                    <vx-button
                        :disabled="!canSubmit"
                        :loading="submitting"
                        type="button"
                        class="grow"
                        size="lg"
                        color="primary"
                        data-test="group-status-submit"
                        @click.prevent="onPrimaryClick"
                    >
                        Update Now
                    </vx-button>
                </template>
            </div>
        </template>
    </vx-modal>

    <schedule-group-status-update-modal
        v-if="scheduleModalVisible && selected !== GROUP_STATUS_DRAFT"
        v-model:visible="scheduleModalVisible"
        :targetStatus="selected"
        :loading="submitting"
        @schedule="submitScheduled"
    />
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendar, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxButton, VxModal, VxSwal } from '@voxie/frontend-components';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupStatusRadios from '~/components/kiosk/kiosk-groups/GroupStatusRadios.vue';
import ScheduleGroupStatusUpdateModal from '~/components/kiosk/kiosk-groups/configuration/ScheduleGroupStatusUpdateModal.vue';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminGroupsClient, groupsClient } from '~/services';
import type { ScheduledStatusChange } from '~/services/adminGroupsClient.types';
import { GROUP_STATUS_CLOSED, GROUP_STATUS_DRAFT, type GroupStatus } from '~/constants/groupStatuses';
import dayjs from '~/utils/dayjs';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';

const props = defineProps<{
    teamId: number;
    groupId: string;
    groupName: string;
}>();

const visible = defineModel<boolean>('visible', { default: false });

const emit = defineEmits<{
    updated: [status: GroupStatus];
}>();

const toasted = useToasted();
const { catchError } = useFormRequestHandler();

const loading = ref(false);
const submitting = ref(false);
const cancelling = ref(false);
const scheduleModalVisible = ref(false);
const currentStatus = ref<GroupStatus | undefined>(undefined);
const isBillable = ref(false);
const selected = ref<GroupStatus>(GROUP_STATUS_DRAFT);
const scheduledChange = ref<ScheduledStatusChange | null>(null);

const canSubmit = computed(
    () => !loading.value && !!currentStatus.value && selected.value !== currentStatus.value && !submitting.value
);

const canSchedule = computed(() => canSubmit.value && selected.value !== GROUP_STATUS_DRAFT);

const scheduledDateFormatted = computed(() => {
    if (!scheduledChange.value?.scheduled_date) {
        return '';
    }
    return dayjs(scheduledChange.value.scheduled_date, 'YYYY-MM-DD').format('M/D/YY');
});

const loadGroup = async () => {
    const response = await groupsClient.getGroup({ teamId: props.teamId }, props.groupId);
    currentStatus.value = response.data.status;
    isBillable.value = Boolean(response.data.is_billable);
    selected.value = response.data.status;
};

const loadScheduledChange = async () => {
    try {
        const response = await adminGroupsClient.getScheduledStatusUpdate(props.teamId, props.groupId);
        scheduledChange.value = response.data;
    } catch (e) {
        if ((e as { response?: { status?: number } })?.response?.status === HTTP_NOT_FOUND) {
            scheduledChange.value = null;
            return;
        }
        throw e;
    }
};

const load = async () => {
    loading.value = true;
    try {
        await Promise.all([loadGroup(), loadScheduledChange()]);
    } catch (e) {
        catchError(e);
        visible.value = false;
    } finally {
        loading.value = false;
    }
};

watch(
    visible,
    (value) => {
        if (value) {
            currentStatus.value = undefined;
            isBillable.value = false;
            scheduledChange.value = null;
            load();
        }
    },
    { immediate: true }
);

const onPrimaryClick = () => {
    if (!canSubmit.value) {
        return;
    }

    if (selected.value === GROUP_STATUS_CLOSED) {
        VxSwal.confirmation(
            'Close Group',
            'Closing this group will release all assigned phone numbers. Released numbers cannot be recovered by this group. This action cannot be undone.',
            'danger',
            () => submitImmediate()
        );
        return;
    }

    submitImmediate();
};

const submitImmediate = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.updateStatus(props.teamId, props.groupId, {
            status: selected.value,
        });
        emit('updated', selected.value);
        toasted.success('Group status updated successfully');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const submitScheduled = async (scheduledDate: string) => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.scheduleStatusUpdate(props.teamId, props.groupId, {
            status: selected.value,
            scheduled_date: scheduledDate,
        });
        scheduleModalVisible.value = false;
        toasted.success('Group status change scheduled');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const cancelScheduled = () => {
    VxSwal.confirmation(
        'Cancel Scheduled Status Change',
        'This will remove the pending status change. You can schedule a new one afterwards.',
        'warning',
        () => submitCancelScheduled()
    );
};

const submitCancelScheduled = async () => {
    if (cancelling.value) {
        return;
    }

    cancelling.value = true;
    try {
        await adminGroupsClient.cancelScheduledStatusUpdate(props.teamId, props.groupId);
        scheduledChange.value = null;
        toasted.success('Scheduled status change cancelled');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        cancelling.value = false;
    }
};
</script>
